// The ECG rhythm strip, shown as a monitor trace: lead II from the synthesiser sweeping across a 5 mm grid at
// 25 mm/s, at the standard 10 mm/mV wherever the box gives a complex a comfortable height (a tablet, a phone on its side,
// the landscape presenter view) and at 20 mm/mV where it would be too small to read (a phone held upright, and the portrait
// presenter view, which goes out on TikTok and Instagram Live to phones); the footer says which.
// The baseline sits where the whole trace fits below the lead label, so no complex is clipped. The heart rate sits at the
// top right of the screen, in the trace's colour, as a bedside monitor or defibrillator shows it: the rate the synthesiser
// draws the rhythm at, --- where a monitor finds none (VF, asystole), and a heart that flashes with each beat. Nothing names
// the rhythm: reading the strip is the question.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { EcgGraphic } from '@shared/types';
import { createEcgStream, profileFor } from './ecg/synth';
import { ecgKey, ecgParams } from './ecg/params';
import { startSweep } from './monitor/sweep';
import { placeBaseline, traceExtent } from './monitor/fit';
import { cssToken } from './env';
import './clinical.css';

const clamp = (x: number, lo: number, hi: number) => (x < lo ? lo : x > hi ? hi : x);

/** What a monitor shows where it can count no beats. */
export const NO_RATE = '---';

/** A 1 mV deflection drawn at 10 mm/mV must stand at least this many layout pixels tall to read; below that the strip draws at 20 mm/mV. */
export const MIN_MV_PX = 32;
/** The gain a strip is drawn at, for its millimetres in layout pixels: the standard 10 mm/mV where a complex reads comfortably, else 20. */
export function stripGain(pxPerMm: number): 10 | 20 {
  return 10 * pxPerMm >= MIN_MV_PX ? 10 : 20;
}
/** A portrait presenter view (1080 layout pixels wide) is streamed to phones about 390 pixels wide. */
export const PORTRAIT_AUDIENCE = 390 / 1080;
/** A landscape presenter view (1920 layout pixels wide) is watched in a laptop window or a stream about 1280 pixels wide. */
export const LANDSCAPE_AUDIENCE = 1280 / 1920;
/**
 * How large the audience sees the strip's layout pixels: a presenter view is watched smaller than it is laid out (the
 * portrait one on phones, the landscape one in a laptop window or a stream), so its complexes are judged at that size and
 * keep 20 mm/mV (16 Oct 2026: at 10 mm/mV the landscape strip's complexes filled a tenth of the box); anywhere else as laid
 * out (a phone, a tablet).
 */
export function audienceScale(el: Element): number {
  const stage = el.closest('[data-stage]');
  if (!stage) return 1;
  // 17 Oct 2026: On air's half layout is 1080 wide too, the bottom half of a portrait stream on a phone.
  const layout = stage.getAttribute('data-layout');
  return layout === 'portrait' || layout === 'half' ? PORTRAIT_AUDIENCE : LANDSCAPE_AUDIENCE;
}
/** The band at the top of the trace that holds the lead label (the trace is kept below it), and the margin at the bottom. */
const LABEL_BAND = 26;
const FOOT_ROOM = 4;

/** The heart rate on the monitor: the rate the synthesiser draws this rhythm at (so it always matches the trace), or NO_RATE. */
export function monitorHr(g: Pick<EcgGraphic, 'rate' | 'rhythm'>): string {
  const rate = profileFor(g).rate;
  return rate > 0 ? String(Math.round(rate)) : NO_RATE;
}

/** Spoken description: what the screen shows, the heart rate included. It never names the rhythm: reading the strip is the question. */
export function stripLabel(g: EcgGraphic): string {
  const hr = monitorHr(g);
  const rate = hr === NO_RATE ? 'The monitor shows no heart rate.' : `Heart rate ${hr} per minute.`;
  return `${g.caption ?? 'ECG rhythm strip, lead II'}. ${rate} Monitor trace at 25 millimetres per second.`;
}

/**
 * The strip's height at a width, as clinical.css lays it out (keep in step with .rx-strip, .rx-strip-head and .rx-strip-foot):
 * the bezel's padding, the monitor's head (6.4% of the screen's width, 30 to 60 px), the 12:5 trace and the footer.
 * The stage uses it to keep a strip inside its box.
 */
export function stripHeight(width: number): number {
  const screen = Math.max(0, width - 2 * 9);
  return 2 * 9 + clamp(screen * 0.064, 30, 60) + (screen * 5) / 12 + 21.2;
}

export function EcgStrip({ graphic }: { graphic: EcgGraphic }) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const grid = useRef<HTMLDivElement>(null);
  const heart = useRef<SVGSVGElement>(null);
  const [gain, setGain] = useState<10 | 20>(10);
  const key = ecgKey(graphic);
  // key stands for everything the trace is drawn from
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => ecgParams(graphic), [key]);
  const hr = monitorHr(graphic);
  useEffect(() => {
    const b = box.current, c = canvas.current;
    if (!b || !c) return;
    const stream = createEcgStream(params);
    const extent = traceExtent(params, 'II');
    const audience = audienceScale(b);
    const colour = cssToken('--trace', '#3DF28A');
    const beat = heart.current;
    return startSweep({
      canvas: c,
      box: b,
      secondsAcross: (w) => (w < 560 ? 6 : clamp(w / 95, 6, 10)),
      lanes: ({ height, pxPerSec }) => {
        const pxPerMm = pxPerSec / 25;
        const mmPerMv = stripGain(pxPerMm * audience);
        const k = mmPerMv * pxPerMm;
        const base = placeBaseline(extent, k, LABEL_BAND, height - FOOT_ROOM, height * 0.6);
        setGain(mmPerMv);
        if (grid.current) {
          grid.current.style.setProperty('--mm', `${pxPerMm}px`);
          grid.current.style.setProperty('--rx-grid-y', `${(base % (pxPerMm * 5)).toFixed(2)}px`);
        }
        return [{ colour, width: 2.1, y: (t: number) => clamp(base - stream.at(t, 'II') * k, LABEL_BAND, height - 2) }];
      },
      // The heart flashes as the pen draws each QRS, as a monitor's does.
      onFrame: (from, to) => {
        if (!beat || typeof beat.animate !== 'function') return;
        if (stream.beatTimes(from, to).length) beat.animate([{ opacity: 1, transform: 'scale(1.3)' }, { opacity: 0.32, transform: 'scale(1)' }], { duration: 300, easing: 'ease-out' });
      },
    });
  }, [params]);
  return (
    <figure className="rx-strip" role="img" aria-label={stripLabel(graphic)}>
      <div className="rx-screen">
        <div className="rx-strip-head" aria-hidden="true">
          <span className="rx-strip-hr" data-none={hr === NO_RATE ? '' : undefined}>
            <span className="rx-strip-hr-label">HR<small>bpm</small></span>
            <span className="rx-strip-hr-value">{hr}</span>
            {hr !== NO_RATE && (
              <svg ref={heart} className="rx-strip-heart" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12 20.6s-7.6-4.7-9.4-9.6C1.4 7.6 3.7 4.4 7 4.4c2.1 0 3.6 1.1 5 3 1.4-1.9 2.9-3 5-3 3.3 0 5.6 3.2 4.4 6.6-1.8 4.9-9.4 9.6-9.4 9.6z" fill="currentColor" />
              </svg>
            )}
          </span>
        </div>
        <div className="rx-strip-trace" ref={box}>
          <div className="rx-strip-grid" ref={grid} />
          <canvas ref={canvas} aria-hidden="true" />
          <span className="rx-lane-label" style={{ top: 9, color: 'var(--trace, #3DF28A)' }}>II</span>
        </div>
      </div>
      <figcaption className="rx-strip-foot" aria-hidden="true">
        <span>{graphic.caption ?? 'Lead II rhythm strip'}</span>
        <span>25 mm/s · {gain} mm/mV</span>
      </figcaption>
    </figure>
  );
}
